import React, { useState, useEffect } from 'react';
import DateFnsUtils from '@date-io/date-fns';
import { makeStyles } from '@material-ui/core/styles';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  Paper,
  Button,
  FormControl,
  InputLabel,
  MenuItem
} from '@material-ui/core';
import {
  ValidatedDatePicker,
  ValidatedSelect,
  Consent,
  Footer
} from '../common';
import { setUserId } from '../../utils/session-utils';
import { withLocal } from '../common/LocaleProvider/LocaleProvider';
import { createUser } from '../../apiCalls/user/user';
import routes from '../../constants/routes';
import markets from '../../constants/markets';
import branches from '../../constants/branches';
import { getMobileNumber } from '../../utils/session-utils';
import useProfile from '../../hooks/useProfile';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px'
  },
  header: {
    margin: '16px 0px',
    fontFamily: 'times',
    color: theme.palette.primary.dark
  },
  subheader: {
    fontSize: '14px'
  },
  infoText: {
    fontSize: '12px',
    margin: '0px 0px 8px 0px'
  },
  alertText: {
    color: '#ca5ce9'
  },
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  picker: {
    '& > div > div > button': {
      display: 'none'
    }
  },
  error: {
    color: '#e02020'
  }
}));

const CreateUser = ({ history, locale }) => {
  const classes = useStyles();
  const mobileNumber = getMobileNumber();

  const { profile, setProfile } = useProfile();

  const getDoShowShowInstructionsChoice = () => {
    const locationState = history.location.state;
    if (locationState && locationState.doNotShowInstructions !== undefined) {
      return locationState.doNotShowInstructions;
    } else {
      return true;
    }
  };

  const getInitialDateOfBirth = () =>
    isNaN(new Date(profile.dob)) ? null : new Date(profile.dob);

  const getInitialMarket = () => {
    const market = markets.find(market => market.value === profile.market);

    return market && market.value;
  };

  const getBranchesFromMarket = market => {
    const marketOptions = branches[market] || [];
    return [...marketOptions];
  };

  const getInitialBranch = market => {
    if (!market) return '';
    const initialBranch = getBranchesFromMarket(market).find(
      branch => branch === profile.branch
    );

    return initialBranch || '';
  };

  const inputLabel = React.useRef(null);
  const inputBranchLabel = React.useRef(null);
  const [labelWidth, setLabelWidth] = React.useState(0);
  const [labelBranchWidth, setLabelBranchWidth] = React.useState(0);
  const [firstName, setFirstName] = useState(profile.firstName || '');
  const [lastName, setLastName] = useState(profile.lastName || '');
  const [dob, setDob] = useState(getInitialDateOfBirth());
  const [userName, setUserName] = useState(profile.userName || '');
  const [email, setEmail] = useState(profile.email || '');
  const [market, setMarket] = useState(getInitialMarket() || '');
  const [branch, setBranch] = useState(getInitialBranch(market) || '');
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [selectError, setSelectError] = useState(false);
  const [selectBranchError, setSelectBranchError] = useState(false);
  const [error, setError] = useState(null);

  const minDate = new Date().getFullYear() - 100;

  useEffect(() => {
    ValidatorForm.addValidationRule('invalidDate', val => {
      return val &&
        (isNaN(val) || val > new Date() || val.getFullYear() < minDate)
        ? false
        : true;
    });
  }, [minDate]);

  useEffect(() => {
    setLabelWidth(inputLabel.current.offsetWidth);
  }, []);

  useEffect(() => {
    if (inputBranchLabel.current) {
      setLabelBranchWidth(inputBranchLabel.current.offsetWidth);
    }
  }, []);

  const onSubmit = () => {
    setError(null);
    setDisableSubmit(true);

    const user = {
      firstName,
      lastName,
      email,
      userName,
      dob,
      market,
      branch,
      mobileNumber
    };

    createUser(
      { ...user, neverShowAgain: getDoShowShowInstructionsChoice() },
      res => {
        setProfile({ ...user });
        setUserId(res.userId);
        history.push(routes.QUESTIONAIRE);
      },
      message => {
        setDisableSubmit(false);
        setError(message);
      }
    );
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <h1 className={classes.header}>{locale.header}</h1>
          <p className={classes.subheader}>{locale.create_user.subheader}</p>
          <ValidatorForm className={classes.form} onSubmit={() => onSubmit()}>
            {error && <span className={classes.error}>{error}</span>}
            <TextValidator
              id="firstName"
              name="firstName"
              className={classes.textfield}
              label={locale.input_labels.first_name}
              value={firstName}
              onChange={e => setFirstName(e.target.value.trim())}
              validators={['required']}
              errorMessages={['this field is required']}
              variant="outlined"
              inputProps={{
                'data-testid': 'first-name-input'
              }}
            />
            <TextValidator
              id="lastName"
              name="lastName"
              className={classes.textfield}
              label={locale.input_labels.last_name}
              value={lastName}
              onChange={e => setLastName(e.target.value.trim())}
              validators={['required']}
              errorMessages={['this field is required']}
              variant="outlined"
              inputProps={{
                'data-testid': 'last-name-input'
              }}
            />
            <ValidatedDatePicker
              disableFuture
              className={[classes.textfield, classes.picker].join(' ')}
              variant="inline"
              inputVariant="outlined"
              label={locale.input_labels.birth_date}
              placeholder="mm/dd/yyyy"
              format="MM/dd/yyyy"
              margin="normal"
              id="date-picker"
              value={dob}
              onChange={date => {
                setDisableSubmit(false);
                setDob(date);
              }}
              inputProps={{
                'data-testid': 'dob-input',
                inputMode: 'numeric'
              }}
              validators={['required', 'invalidDate']}
              errorMessages={['this field is required', 'date is invalid']}
            />
            <TextValidator
              id="userName"
              name="userName"
              className={classes.textfield}
              style={{ marginBottom: '0px' }}
              label={locale.input_labels.user_name}
              value={userName}
              onChange={e => setUserName(e.target.value.trim())}
              validators={['required']}
              errorMessages={['this field is required']}
              variant="outlined"
              inputProps={{
                'data-testid': 'user-name-input'
              }}
            />
            <p className={classes.infoText}>{locale.create_user.domain_info}</p>
            <TextValidator
              id="email"
              name="email"
              className={classes.textfield}
              label={locale.input_labels.email}
              value={email}
              onChange={e => setEmail(e.target.value.trim())}
              validators={['required', 'isEmail']}
              errorMessages={['this field is required', 'email is invaild']}
              variant="outlined"
              inputProps={{
                'data-testid': 'email-input'
              }}
            />
            <FormControl
              variant="outlined"
              className={classes.textfield}
              error={selectError}
            >
              <InputLabel ref={inputLabel} id="market-select-label">
                {locale.input_labels.local_ministry}
              </InputLabel>
              <ValidatedSelect
                labelId="market-select-label"
                id="market-select"
                value={market}
                onChange={e => {
                  setSelectError(false);
                  setDisableSubmit(true);
                  setBranch('');
                  setMarket(e.target.value);
                }}
                labelWidth={labelWidth}
                validators={['required']}
                errorMessages={['this field is required']}
                inputProps={{
                  'data-testid': 'market-select'
                }}
              >
                <MenuItem value={''}>
                  <em>{locale.input_labels.local_ministry}</em>
                </MenuItem>
                {markets.map(market => (
                  <MenuItem
                    key={`market-select-${market.value}`}
                    value={market.value}
                  >
                    {market.display}
                  </MenuItem>
                ))}
              </ValidatedSelect>
            </FormControl>
            <FormControl
              variant="outlined"
              className={classes.textfield}
              error={selectBranchError}
            >
              <InputLabel ref={inputBranchLabel} id="branch-select-label">
                {locale.input_labels.branching_hospital}
              </InputLabel>
              <ValidatedSelect
                labelId="branch-select-label"
                id="branch-select"
                disabled={market === ''}
                value={branch}
                onChange={e => {
                  setSelectBranchError(false);
                  setDisableSubmit(e.target.value === '');
                  setBranch(e.target.value);
                }}
                labelWidth={labelBranchWidth}
                validators={['required']}
                errorMessages={['this field is required']}
                inputProps={{
                  'data-testid': 'branch-select'
                }}
              >
                <MenuItem value={''}>
                  <em>{locale.input_labels.branching_hospital}</em>
                </MenuItem>
                {getBranchesFromMarket(market).map(possible_branch => (
                  <MenuItem
                    key={`branch-select-${possible_branch}`}
                    value={possible_branch}
                  >
                    {possible_branch}
                  </MenuItem>
                ))}
              </ValidatedSelect>
            </FormControl>
            <Button
              id="btn-send-validate"
              data-testid="submit-button"
              className={classes.button}
              type="submit"
              color="primary"
              variant="contained"
              disabled={disableSubmit}
            >
              {locale.button_labels.next}
            </Button>
          </ValidatorForm>
          <Consent style={{ marginTop: '40px' }} locale={locale} />
          <Footer locale={locale} />
        </Paper>
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default withLocal(CreateUser);
